import { Send } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { BottomSheet } from '../../../../../../components/molecules/BottomSheet';
import { MobileAddNoteDialogProps } from './MobileAddNoteDialog.props';
import {
  InputWrapper,
  NotesTextField,
  CharacterCount,
  ErrorMessage,
  NoteContent,
  CancelButton,
  SubmitButton,
} from './MobileAddNoteDialog.styles';
import { useAddServiceLogsForService } from '../../../../../../queries/service-log-query';

const MAX_CHARACTERS = 1000;

export const MobileAddNoteDialog: React.FC<MobileAddNoteDialogProps> = ({
  open,
  onClose,
  serviceId,
  onNoteAdded,
}) => {
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const addNoteMutation = useAddServiceLogsForService(serviceId);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setNotes(value);
      if (error) setError(null);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedNotes = notes.trim();
    if (!trimmedNotes || addNoteMutation.isPending) return;

    setError(null);

    try {
      await addNoteMutation.mutateAsync({
        notes: trimmedNotes,
        serviceId: serviceId,
      });

      setNotes('');
      await onNoteAdded();
      onClose();
    } catch (err) {
      console.error('Failed to add note:', err);
      setError('Failed to add note. Please try again.');
    }
  };

  const handleClose = (): void => {
    setNotes('');
    setError(null);
    onClose();
  };

  const characterCount = notes.length;
  const isNearLimit = characterCount > MAX_CHARACTERS * 0.9;
  const canSubmit = notes.trim().length > 0 && !addNoteMutation.isPending;

  const footer = (
    <>
      <CancelButton onClick={handleClose} variant="outlined" fullWidth>
        Cancel
      </CancelButton>
      <SubmitButton
        variant="contained"
        onClick={handleSubmit}
        disabled={!canSubmit}
        startIcon={
          addNoteMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <Send />
        }
        fullWidth
      >
        {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
      </SubmitButton>
    </>
  );

  return (
    <BottomSheet open={open} onClose={handleClose} height="small" title="Add Note" footer={footer}>
      <NoteContent>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <InputWrapper>
          <NotesTextField
            fullWidth
            multiline
            rows={6}
            value={notes}
            onChange={handleNotesChange}
            placeholder="Enter notes about this service..."
            disabled={addNoteMutation.isPending}
            inputRef={inputRef}
            label="Note"
          />
          <CharacterCount isNearLimit={isNearLimit}>
            {characterCount}/{MAX_CHARACTERS}
          </CharacterCount>
        </InputWrapper>
      </NoteContent>
    </BottomSheet>
  );
};
